import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplyIcon from '@mui/icons-material/Reply';
import DoneIcon from '@mui/icons-material/Done';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService } from '../../utils/api';
import axios from 'axios';
import config from '../../config';

const violationTypesMap = {
  'harassment': '攻撃的な行為・嫌がらせ',
  'violence': '暴力的な発言',
  'privacy': 'プライバシーの侵害',
  'spam': 'スパム',
  'impersonation': 'なりすまし',
  'solicitation': '勧誘',
  'other': 'その他'
};

const ReportManagement = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // 通報データの取得
  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      // APIリクエストを送信
      const response = await axios.get(`${config.API_BASE_URL}/api/reports?page=${currentPage}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        timeout: 10000
      });
      
      // レスポンスのデータ構造をログに出力（デバッグ用）
      
      
      // データが配列として返ってきたかを確認
      if (response?.data && response.data?.status === 'success') {
        if (Array.isArray(response.data?.reports)) {
          const reportsData = response.data.reports;
          setReports(reportsData);
          setTotalPages(response.data.pages || 1);
        } else if (response.data?.reports && typeof response.data.reports === 'object') {
          // レポートがオブジェクト形式で返ってきた場合は配列に変換
          
          const reportsArray = Object.values(response.data.reports);
          setReports(reportsArray);
          setTotalPages(response.data.pages || 1);
        } else {
          
          setReports([]);
          setTotalPages(1);
        }
      } else {
        // レスポンスの形式が期待と異なる場合
        
        setReports([]);
        setTotalPages(1);
        throw new Error(response?.data?.message || 'APIからのレスポンス形式が正しくありません');
      }
    } catch (error) {
      // ネットワークエラーの詳細をログに出力
      
      
      
      setError(error.message || '不明なエラー');
      
      if (error.message === 'Network Error') {
        
        setSnackbar({
          open: true,
          message: 'サーバーに接続できません。システム管理者にお問い合わせください。',
          severity: 'error'
        });
      } else {
        setSnackbar({
          open: true,
          message: `通報データの取得に失敗しました: ${error.message || '不明なエラー'}`,
          severity: 'error'
        });
      }
      
      // エラー発生時は空の配列を設定
      setReports([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // ダミーデータの生成関数（削除しない - 将来の開発で必要になる可能性あり）
  const generateDummyReports = () => {
    return [
      {
        id: 1,
        reporter_username: 'user123',
        reported_username: 'baduser1',
        violation_type: 'harassment',
        incident_time: '2023-10-15T14:30:00',
        description: '攻撃的なコメントを繰り返しています。',
        status: 'pending',
        created_at: '2023-10-15T15:00:00',
        updated_at: null
      },
      {
        id: 2,
        reporter_username: 'user456',
        reported_username: 'spammer42',
        violation_type: 'spam',
        incident_time: '2023-10-14T10:15:00',
        description: '同じ宣伝メッセージを複数の投稿に送信しています。',
        status: 'resolved',
        created_at: '2023-10-14T11:20:00',
        updated_at: '2023-10-15T09:30:00'
      },
      {
        id: 3,
        reporter_username: 'user789',
        reported_username: 'fake_account',
        violation_type: 'impersonation',
        incident_time: '2023-10-13T16:45:00',
        description: '有名人になりすましています。プロフィール画像も公式アカウントから盗用しています。',
        status: 'pending',
        created_at: '2023-10-13T17:30:00',
        updated_at: null
      }
    ];
  };

  // 初回レンダリング時と現在のページが変更されたときに通報データを取得
  useEffect(() => {
    // API実装が完了するまでの暫定対応として、ダミーデータを使用
    fetchReports();
    // setReports(generateDummyReports());
    // setLoading(false);
  }, [currentPage]);

  // 通報詳細表示
  const handleViewDetails = (report) => {
    setSelectedReport(report);
    setDialogOpen(true);
  };

  // 返信ダイアログを開く
  const handleOpenReply = (report) => {
    setSelectedReport(report);
    setReplyDialogOpen(true);
  };

  // 返信送信
  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setSnackbar({
        open: true,
        message: '返信内容を入力してください',
        severity: 'error'
      });
      return;
    }

    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/reports/${selectedReport.id}/reply`, {
        comment: replyMessage
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.data && response.data.status === 'success') {
        const updatedReports = reports.map(report => 
          report.id === selectedReport.id 
            ? { ...report, status: 'resolved', admin_comment: replyMessage, updated_at: new Date().toISOString() }
            : report
        );
        setReports(updatedReports);
        
        setSnackbar({
          open: true,
          message: '返信を送信しました',
          severity: 'success'
        });
        
        setReplyMessage('');
        setReplyDialogOpen(false);
        setSelectedReport(null);
      } else {
        throw new Error(response.data?.message || '返信の送信に失敗しました');
      }
    } catch (error) {
      
      setSnackbar({
        open: true,
        message: `返信の送信に失敗しました: ${error.message || '不明なエラー'}`,
        severity: 'error'
      });
    }
  };

  // 解決済みとしてマーク
  const handleMarkResolved = async (reportId) => {
    try {
      const response = await axios.put(`${config.API_BASE_URL}/api/reports/${reportId}/status`, {
        status: 'resolved'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.data && response.data.status === 'success') {
        const updatedReports = reports.map(report => 
          report.id === reportId 
            ? { ...report, status: 'resolved', updated_at: new Date().toISOString() }
            : report
        );
        
        setReports(updatedReports);
        
        setSnackbar({
          open: true,
          message: '通報を解決済みとしてマークしました',
          severity: 'success'
        });
      } else {
        throw new Error(response.data?.message || 'ステータス更新に失敗しました');
      }
    } catch (error) {
      
      setSnackbar({
        open: true,
        message: `ステータスの更新に失敗しました: ${error.message || '不明なエラー'}`,
        severity: 'error'
      });
    }
  };

  // 未対応に戻す
  const handleMarkPending = async (reportId) => {
    try {
      const response = await axios.put(`${config.API_BASE_URL}/api/reports/${reportId}/status`, {
        status: 'pending'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.data && response.data.status === 'success') {
        const updatedReports = reports.map(report => 
          report.id === reportId 
            ? { ...report, status: 'pending', updated_at: new Date().toISOString() }
            : report
        );
        
        setReports(updatedReports);
        
        setSnackbar({
          open: true,
          message: '通報を未対応としてマークしました',
          severity: 'success'
        });
      } else {
        throw new Error(response.data?.message || 'ステータス更新に失敗しました');
      }
    } catch (error) {
      
      setSnackbar({
        open: true,
        message: `ステータスの更新に失敗しました: ${error.message || '不明なエラー'}`,
        severity: 'error'
      });
    }
  };

  // 削除確認ダイアログを開く
  const handleOpenDeleteConfirm = (report) => {
    setReportToDelete(report);
    setDeleteConfirmOpen(true);
  };

  // 通報削除
  const handleDeleteReport = async () => {
    if (!reportToDelete) return;

    try {
      const response = await axios.delete(`${config.API_BASE_URL}/api/reports/${reportToDelete.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.data && response.data.status === 'success') {
        const filteredReports = reports.filter(report => report.id !== reportToDelete.id);
        setReports(filteredReports);
        
        setSnackbar({
          open: true,
          message: '通報を削除しました',
          severity: 'success'
        });
        
        setDeleteConfirmOpen(false);
        setReportToDelete(null);
      } else {
        throw new Error(response.data?.message || '通報の削除に失敗しました');
      }
    } catch (error) {
      
      setSnackbar({
        open: true,
        message: `通報の削除に失敗しました: ${error.message || '不明なエラー'}`,
        severity: 'error'
      });
    }
  };

  // 前のページに移動
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // 次のページに移動
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // スナックバーを閉じる
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 日付フォーマット
  const formatDate = (dateString) => {
    if (!dateString) return '不明';
    
    try {
    const date = new Date(dateString);
      return date.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '日付形式エラー';
    }
  };

  // 画面レンダリング
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        報告管理
      </Typography>

      {/* エラー表示 */}
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.dark' }}>
          <Typography variant="body1">
            エラーが発生しました: {error}
            </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            sx={{ mt: 1 }} 
            onClick={fetchReports}
          >
            再試行
          </Button>
        </Paper>
      )}
      
      {/* 通報一覧テーブル */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>通報者</TableCell>
              <TableCell>通報対象者</TableCell>
              <TableCell>違反の種類</TableCell>
              <TableCell>状態</TableCell>
              <TableCell>通報日時</TableCell>
              <TableCell>管理者返信</TableCell>
              <TableCell align="center">アクション</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>データを読み込み中...</Typography>
                </TableCell>
              </TableRow>
            ) : reports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography>通報はありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              reports.map((report) => (
                <TableRow key={report.id} hover>
                    <TableCell>{report.id}</TableCell>
                    <TableCell>{report.reporter_username}</TableCell>
                    <TableCell>{report.reported_username}</TableCell>
                    <TableCell>
                      {violationTypesMap[report.violation_type] || report.violation_type}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={report.status === 'pending' ? '対応待ち' : '解決済み'} 
                        color={report.status === 'pending' ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(report.created_at)}</TableCell>
                  <TableCell>
                    {report.admin_comment ? (
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {report.admin_comment}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                        <IconButton 
                      color="primary" 
                          size="small"
                          onClick={() => handleViewDetails(report)}
                      title="詳細を表示"
                        >
                      <VisibilityIcon />
                        </IconButton>
                        {report.status === 'pending' ? (
                          <>
                            <IconButton 
                          color="success" 
                              size="small"
                          onClick={() => handleMarkResolved(report.id)}
                          title="解決済みにする"
                            >
                          <DoneIcon />
                            </IconButton>
                            <IconButton 
                          color="info" 
                              size="small"
                          onClick={() => handleOpenReply(report)}
                          title="返信する"
                            >
                          <ReplyIcon />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton 
                        color="warning" 
                            size="small"
                        onClick={() => handleMarkPending(report.id)}
                            title="未対応に戻す"
                          >
                        <UndoIcon />
                          </IconButton>
                        )}
                        <IconButton 
                      color="error" 
                          size="small" 
                      onClick={() => handleOpenDeleteConfirm(report)}
                          title="削除"
                        >
                      <DeleteIcon />
                        </IconButton>
                    </TableCell>
                  </TableRow>
              ))
            )}
              </TableBody>
            </Table>
        </TableContainer>
        
      {/* ページネーション */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button 
          disabled={currentPage <= 1} 
            onClick={handlePrevPage} 
          sx={{ mr: 2 }}
          >
          前のページ
          </Button>
        <Typography sx={{ lineHeight: '36px' }}>
          {currentPage} / {totalPages}ページ
          </Typography>
          <Button 
          disabled={currentPage >= totalPages} 
            onClick={handleNextPage} 
          sx={{ ml: 2 }}
          >
          次のページ
          </Button>
        </Box>

      {/* 詳細ダイアログ */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedReport && (
          <>
            <DialogTitle>
              通報詳細
              <IconButton
                aria-label="close"
                onClick={() => setDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">通報ID</Typography>
                  <Typography variant="body1">#{selectedReport.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">ステータス</Typography>
                  <Chip 
                    label={selectedReport.status === 'pending' ? '対応待ち' : '解決済み'} 
                    color={selectedReport.status === 'pending' ? 'warning' : 'success'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">通報者</Typography>
                  <Typography variant="body1">{selectedReport.reporter_username}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">通報された日時</Typography>
                  <Typography variant="body1">{formatDate(selectedReport.created_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">通報対象者</Typography>
                  <Typography variant="body1">{selectedReport.reported_username}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">違反が起きた日時</Typography>
                  <Typography variant="body1">{formatDate(selectedReport.incident_time)}</Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / 3' }}>
                  <Typography variant="subtitle2" color="textSecondary">違反の種類</Typography>
                  <Typography variant="body1">
                    {violationTypesMap[selectedReport.violation_type] || selectedReport.violation_type}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: '1 / 3' }}>
                  <Typography variant="subtitle2" color="textSecondary">詳細</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                    <Typography variant="body1">{selectedReport.description || '詳細情報はありません。'}</Typography>
                  </Paper>
                </Box>
              </Box>
              
              {selectedReport.status === 'resolved' && selectedReport.updated_at && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">解決日時</Typography>
                  <Typography variant="body1">{formatDate(selectedReport.updated_at)}</Typography>
                </Box>
              )}
              
              {selectedReport.admin_comment && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">管理者返信</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: '#f0f7ff' }}>
                    <Typography variant="body1">{selectedReport.admin_comment}</Typography>
                  </Paper>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {selectedReport.status === 'pending' ? (
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<ReplyIcon />}
                  onClick={() => {
                    setDialogOpen(false);
                    setReplyDialogOpen(true);
                  }}
                >
                  返信する
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<UndoIcon />}
                  onClick={() => {
                    handleMarkPending(selectedReport.id);
                    setDialogOpen(false);
                  }}
                >
                  未対応に戻す
                </Button>
              )}
              <Button 
                variant="outlined" 
                onClick={() => setDialogOpen(false)}
              >
                閉じる
              </Button>
              <Button 
                variant="contained" 
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  setDialogOpen(false);
                  handleOpenDeleteConfirm(selectedReport);
                }}
              >
                削除
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 返信ダイアログ */}
      <Dialog 
        open={replyDialogOpen} 
        onClose={() => setReplyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedReport && (
          <>
            <DialogTitle>
              通報への返信
              <IconButton
                aria-label="close"
                onClick={() => setReplyDialogOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">通報者</Typography>
                <Typography variant="body1">{selectedReport.reporter_username}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">違反の種類</Typography>
                <Typography variant="body1">
                  {violationTypesMap[selectedReport.violation_type] || selectedReport.violation_type}
                </Typography>
              </Box>
              <TextField
                autoFocus
                margin="dense"
                label="返信メッセージ"
                type="text"
                fullWidth
                multiline
                rows={4}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="通報者に送る返信内容をここに入力してください。このメッセージは通報者の通知に表示されます。"
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                * この返信を送信すると、通報は自動的に「解決済み」としてマークされます。
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReplyDialogOpen(false)}>キャンセル</Button>
              <Button onClick={handleSendReply} variant="contained" color="primary">
                送信して解決済みにする
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* 通報削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>通報の削除</DialogTitle>
        <DialogContent>
          <Typography>
            この通報を削除してもよろしいですか？この操作は取り消せません。
          </Typography>
          {reportToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>通報者:</strong> {reportToDelete.reporter_username}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>対象ユーザー:</strong> {reportToDelete.reported_username}
              </Typography>
              <Typography variant="body2">
                <strong>違反種類:</strong> {violationTypesMap[reportToDelete.violation_type] || reportToDelete.violation_type}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>キャンセル</Button>
          <Button onClick={handleDeleteReport} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー通知 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportManagement; 